import type { Comment, Thread } from '@atlas/api-contract';

import { screenPathWithoutHash } from '@/features/viewer/screen-paths';

/**
 * Everything the panel and the popover have to *derive* from a thread, kept
 * pure so it can be tested without a DOM and reused by both.
 *
 * The panel row and the pin marker must agree exactly — the same initial, the
 * same reply count, the same screen name — or hovering a row would visibly
 * highlight a pin that says something different. Deriving both from these
 * functions is what makes that agreement structural rather than a convention.
 */

/**
 * The single character shown in a pin marker and beside a comment.
 *
 * Taken from the first *grapheme-ish* character rather than `name[0]`: a
 * surrogate pair sliced in half renders as a replacement glyph, and an author
 * called "🙂 Sam" would get a broken box on every one of their comments.
 * `Intl.Segmenter` is not used because this runs on every row of every render
 * and the array spread is exact enough for a single leading character.
 */
export const authorInitial = (name: string): string => {
  const trimmed = name.trim();

  if (trimmed === '') {
    return '?';
  }

  return ([...trimmed][0] ?? '?').toUpperCase();
};

/**
 * The first line of a comment, for the panel row.
 *
 * A review comment routinely opens with one sentence and then three paragraphs
 * of detail; the row shows the sentence. Any run of whitespace containing a
 * newline ends the line, so a body that starts with a blank line does not
 * produce an empty row.
 */
export const firstLine = (body: string): string => {
  const trimmed = body.trim();

  if (trimmed === '') {
    return '';
  }

  const newline = trimmed.search(/[\r\n]/);
  const line = newline === -1 ? trimmed : trimmed.slice(0, newline);

  return line.trim();
};

/** The comment that opened the thread, or `null` for an empty one. */
export const threadFirstComment = (thread: Thread): Comment | null =>
  thread.comments[0] ?? null;

/** Every comment after the first, in the order they were written. */
export const threadReplies = (thread: Thread): Comment[] =>
  thread.comments.slice(1);

/**
 * Replies, from `commentCount` rather than from `comments.length`.
 *
 * They are the same number today. `commentCount` is used because it is the
 * server's own count and stays correct if the list is ever truncated, and
 * because it is what the pin marker is drawn from — a marker and a row
 * disagreeing about "2 replies" is exactly the kind of small lie that makes a
 * review tool feel untrustworthy.
 */
export const threadReplyCount = (thread: Thread): number =>
  Math.max(0, thread.commentCount - 1);

/** Whether a comment is the one that opened its thread. */
export const isFirstComment = (thread: Thread, commentId: string): boolean =>
  threadFirstComment(thread)?.id === commentId;

/**
 * What the delete confirmation has to say (spec R5.5).
 *
 * Deleting a reply deletes a reply. Deleting the comment that *opened* the
 * thread deletes the thread, its pin, and every reply anyone else has written
 * under it — so the confirmation names that consequence and counts the replies,
 * because "this cannot be undone" does not tell the person clicking it that
 * they are about to destroy three colleagues' answers.
 */
export const describeCommentDeletion = (
  isFirst: boolean,
  replyCount: number,
): string => {
  if (!isFirst) {
    return 'This deletes the reply. It cannot be undone.';
  }

  if (replyCount === 0) {
    return 'This is the comment that opened the thread, so deleting it deletes the whole thread and removes its pin. It cannot be undone.';
  }

  return `This is the comment that opened the thread, so deleting it deletes the whole thread, removes its pin, and takes ${String(replyCount)} ${replyCount === 1 ? 'reply' : 'replies'} with it. It cannot be undone.`;
};

/* -------------------------------------------------------------------------- */
/* Screens                                                                     */
/* -------------------------------------------------------------------------- */

const withLeadingSlash = (value: string): string =>
  value === '' ? '/' : value.startsWith('/') ? value : `/${value}`;

/** True when `long` equals `short` or ends with it on a path boundary. */
const endsOnBoundary = (long: string, short: string): boolean => {
  if (long === short) {
    return true;
  }

  if (long.length <= short.length) {
    return false;
  }

  return long.endsWith(short) && short.startsWith('/');
};

/**
 * Whether a thread's pin belongs on the screen currently displayed.
 *
 * This is deliberately the *same* rule the agent applies in `screenPathMatches`
 * — if the parent and the agent disagreed, the panel would list a thread whose
 * pin the agent refuses to draw, or worse, hide a row for a pin sitting on
 * screen. Both sides therefore layer the same two tolerances on equality:
 *
 *  1. A path recorded relative to the version root (`/settings.html`) matches
 *     the served path (`/<prototypeId>/<versionId>/settings.html`), because the
 *     agent reports `location.pathname` and the version prefix is constant
 *     within a version anyway.
 *  2. A pin created with **no** hash stays on screen after the prototype jumps
 *     to an in-page anchor, while a pin created **with** a hash is compared in
 *     full — which is what keeps the screens of a hash-routed single-page
 *     prototype apart (spec R3.4).
 *
 * A third tolerance layers on top for single-URL client-side apps, whose
 * `screenPath` never changes: a thread also has to be on the *same client-side
 * screen*, identified by the agent's `screenKey`. A thread with a `null`
 * `screenKey` (every classic URL-navigated prototype, and pins placed on the
 * initial screen) is unconstrained by it, so passing the keys is a no-op for
 * classic prototypes and the path rules above are the whole test. When the
 * thread carries a key it must equal the key of the screen on display.
 */
export const threadScreenMatches = (
  threadPath: string,
  currentPath: string | null,
  threadScreenKey: string | null = null,
  currentScreenKey: string | null = null,
): boolean => {
  if (currentPath === null) {
    return false;
  }

  const pin = withLeadingSlash(threadPath.trim());
  const current = withLeadingSlash(currentPath.trim());

  const pathMatches =
    pin === current
      ? true
      : pin.includes('#')
        ? endsOnBoundary(current, pin)
        : endsOnBoundary(screenPathWithoutHash(current), pin);

  if (!pathMatches) {
    return false;
  }

  return threadScreenKey === null || threadScreenKey === currentScreenKey;
};

/**
 * A human name for the screen a thread is pinned to, e.g. `settings`.
 *
 * `basePath` is the version's own URL path, which is stripped first: a thread
 * records `location.pathname`, so its raw `screenPath` is
 * `/<prototypeId>/<versionId>/settings.html` and showing that in a panel row
 * would be two UUIDs of noise around the one word that matters.
 *
 * The version root — the screen served by the bare version URL — has no
 * filename at all, so it falls back to the version's recorded `entryPath` and
 * then to a written label rather than rendering as blank.
 */
export const toThreadScreenLabel = (
  screenPath: string,
  basePath: string | null,
  entryPath: string | null = null,
): string => {
  const withoutHash = screenPathWithoutHash(screenPath);
  const base = basePath === null ? null : screenPathWithoutHash(basePath);

  const relative =
    base !== null && base !== '/' && withoutHash.startsWith(base)
      ? withoutHash.slice(base.length)
      : withoutHash;

  const file = relative.slice(relative.lastIndexOf('/') + 1);

  if (file === '') {
    const entryFile =
      entryPath === null
        ? ''
        : entryPath.slice(entryPath.lastIndexOf('/') + 1);

    return entryFile === ''
      ? 'Entry screen'
      : entryFile.replace(/\.html?$/i, '');
  }

  return file.replace(/\.html?$/i, '');
};
